import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getForecasts, deleteForecast, getForecastDashboard } from '../api';
import ConfirmDialog from './ConfirmDialog';
import useConfirm from '../hooks/useConfirm';
import { useTheme } from '../hooks/useTheme';

/* ═══════════════════════════════════════════════
   DESIGN SYSTEM — Mastt-inspired construction dashboard
   ═══════════════════════════════════════════════ */
const C = {
  indigo:    '#6366f1', indigoL:  '#a5b4fc', indigoBg:  'rgba(99,102,241,.08)',
  emerald:   '#10b981', emeraldL: '#6ee7b7', emeraldBg: 'rgba(16,185,129,.08)',
  amber:     '#f59e0b', amberL:   '#fcd34d', amberBg:   'rgba(245,158,11,.08)',
  rose:      '#f43f5e', roseL:    '#fda4af',
  slate900:  '#0f172a', slate700: '#334155', slate500:  '#64748b',
  slate300:  '#cbd5e1', slate100: '#f1f5f9', white:     '#ffffff',
  grid:      '#f1f5f9', border:   '#e2e8f0',
};
const CAT_COLORS = {
  'Labor':              { s: '#6366f1', l: '#c7d2fe' },
  'Material':           { s: '#f97316', l: '#fed7aa' },
  'Equipment':          { s: '#10b981', l: '#a7f3d0' },
  'General Conditions': { s: '#64748b', l: '#cbd5e1' },
  'Other':              { s: '#8b5cf6', l: '#ddd6fe' },
};
const gc = (c) => CAT_COLORS[c] || { s: '#94a3b8', l: '#e2e8f0' };

/* ═══ Formatters ═══ */
const $s = (v) => { const n = +v || 0; if (n >= 1e6) return `$${(n/1e6).toFixed(1)}M`; if (n >= 1e3) return `$${(n/1e3).toFixed(0)}K`; return `$${n.toFixed(0)}`; };
const $f = (v) => new Intl.NumberFormat('en-US',{style:'currency',currency:'USD'}).format(+v||0);
const fM = (ym) => { if(!ym) return ''; const [y,m]=ym.split('-'); return `${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][+m-1]} '${y.slice(2)}`; };

/* ═══════════════════════════════════════════════
   PROGRESS RING — Mastt-style donut KPI
   ═══════════════════════════════════════════════ */
const ProgressRing = ({ pct, size = 72, stroke = 7, color = C.indigo, label, value, sub, subExtra, t }) => {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const filled = Math.min(Math.max(pct, 0), 100);
  const ringBg = t?.dark ? 'rgba(255,255,255,.08)' : C.slate100;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', flexShrink: 0 }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={ringBg} strokeWidth={stroke} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={circ} strokeDashoffset={circ - (filled/100)*circ}
          strokeLinecap="round" style={{ transition: 'stroke-dashoffset .6s ease' }} />
        <text x={size/2} y={size/2} textAnchor="middle" dominantBaseline="central"
          style={{ transform: 'rotate(90deg)', transformOrigin: '50% 50%' }}
          fill={t?.heading || C.slate700} fontSize={size < 60 ? 11 : 14} fontWeight={800} fontFamily="system-ui">
          {Math.round(filled)}%
        </text>
      </svg>
      <div>
        <div style={{ fontSize: 10, fontWeight: 700, color: t?.textSecondary || C.slate500, textTransform: 'uppercase', letterSpacing: .8 }}>{label}</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: t?.heading || C.slate900, marginTop: 2 }}>{value}</div>
        {sub && <div style={{ fontSize: 11, color: t?.textSecondary || C.slate500, marginTop: 1 }}>{sub}</div>}
        {subExtra && <div style={{ fontSize: 11, color: '#d97706', marginTop: 1 }}>{subExtra}</div>}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   FLOATING TOOLTIP
   ═══════════════════════════════════════════════ */
const Tip = ({ info }) => {
  if (!info) return null;
  return (
    <div style={{
      position:'absolute', left:info.x, top:info.y, transform:'translate(-50%,-120%)',
      pointerEvents:'none', zIndex:30, background:C.slate900, color:'#fff',
      padding:'10px 16px', borderRadius:12, fontSize:12, fontWeight:600,
      boxShadow:'0 12px 32px rgba(0,0,0,.2)', whiteSpace:'nowrap',
      backdropFilter:'blur(8px)', transition:'left .06s,top .06s',
    }}>
      {info.title && <div style={{ fontSize:11, color:C.slate300, marginBottom:6, fontWeight:700, textTransform:'uppercase', letterSpacing:.5 }}>{info.title}</div>}
      {info.rows.map((r,i)=>(
        <div key={i} style={{ display:'flex', alignItems:'center', gap:8, marginTop:i?5:0 }}>
          <span style={{ width:8, height:8, borderRadius:'50%', background:r.color, flexShrink:0 }} />
          <span style={{ color:C.slate300, minWidth:70 }}>{r.label}</span>
          <span style={{ fontWeight:700 }}>{r.value}</span>
        </div>
      ))}
      <div style={{ position:'absolute', bottom:-6, left:'50%', transform:'translateX(-50%)', width:0, height:0,
        borderLeft:'7px solid transparent', borderRight:'7px solid transparent', borderTop:`7px solid ${C.slate900}` }} />
    </div>
  );
};

/* ═══════════════════════════════════════════════
   AREA BAR CHART — Monthly forecast with area overlay
   ═══════════════════════════════════════════════ */
const useContainerWidth = (ref, fallback = 800) => {
  const [w, setW] = useState(fallback);
  useEffect(() => {
    if (!ref.current) return;
    const measure = () => { const cw = ref.current?.clientWidth; if (cw && cw > 100) setW(cw); };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref]);
  return w;
};

const MonthlyChart = ({ months, forecast, actuals, pending, maxVal, t }) => {
  const ref = useRef(null);
  const [tip, setTip] = useState(null);
  const [hI, setHI] = useState(-1);
  const cW = useContainerWidth(ref);

  const P = {t:28,r:16,b:48,l:62};
  const H = 300, W = Math.max(months.length * 72, cW);
  const iW = W-P.l-P.r, iH = H-P.t-P.b;
  const gap = iW / months.length;
  const bW = Math.min(24, gap*.38);

  const show = (e, m) => {
    const rc = ref.current.getBoundingClientRect();
    setTip({ x:e.clientX-rc.left, y:e.clientY-rc.top, title:fM(m), rows:[
      { label:'Projected', value:$f(forecast[m]||0), color:C.indigo },
      ...(pending[m]?[{label:'Pending',value:$f(pending[m]),color:C.amber}]:[]),
    ]});
  };

  // Build forecast area path
  const areaPts = months.map((m,i) => {
    const x = P.l + i*gap + gap/2;
    const fV = forecast[m]||0;
    const y = P.t + iH - (fV/maxVal)*iH;
    return {x,y};
  });
  const areaPath = areaPts.length > 1
    ? `M${areaPts[0].x},${P.t+iH} ` + areaPts.map((p,i) => {
        if (i === 0) return `L${p.x},${p.y}`;
        const prev = areaPts[i-1];
        const cpx = (prev.x+p.x)/2;
        return `C${cpx},${prev.y} ${cpx},${p.y} ${p.x},${p.y}`;
      }).join(' ') + ` L${areaPts[areaPts.length-1].x},${P.t+iH} Z`
    : '';
  const linePath = areaPts.length > 1
    ? areaPts.map((p,i) => {
        if (i === 0) return `M${p.x},${p.y}`;
        const prev = areaPts[i-1];
        const cpx = (prev.x+p.x)/2;
        return `C${cpx},${prev.y} ${cpx},${p.y} ${p.x},${p.y}`;
      }).join(' ')
    : '';

  return (
    <div ref={ref} style={{ position:'relative', overflowX:'auto', overflowY:'visible' }}>
      <Tip info={tip} />
      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMinYMid meet" style={{ display:'block', minWidth:months.length>8?W:'auto' }}>
        <defs>
          <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.indigo} stopOpacity=".14" />
            <stop offset="100%" stopColor={C.indigo} stopOpacity=".01" />
          </linearGradient>
          <linearGradient id="gBar" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.indigo} stopOpacity=".9" />
            <stop offset="100%" stopColor={C.indigoL} stopOpacity=".7" />
          </linearGradient>
          <linearGradient id="gBarA" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.emerald} />
            <stop offset="100%" stopColor={C.emeraldL} />
          </linearGradient>
          <linearGradient id="gBarP" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.amber} />
            <stop offset="100%" stopColor={C.amberL} />
          </linearGradient>
          <filter id="bs"><feDropShadow dx="0" dy="3" stdDeviation="4" floodOpacity=".1" /></filter>
        </defs>

        {/* Grid */}
        {Array.from({length:6},(_,i)=>{
          const y=P.t+iH-(i/5)*iH;
          const gridColor = t?.dark ? 'rgba(255,255,255,.06)' : C.grid;
          const borderColor = t?.dark ? 'rgba(255,255,255,.12)' : C.border;
          return <g key={i}>
            <line x1={P.l} x2={W-P.r} y1={y} y2={y} stroke={i===0?borderColor:gridColor} />
            <text x={P.l-10} y={y+4} textAnchor="end" fill={t?.textSecondary||C.slate500} fontSize={10} fontFamily="system-ui">{$s((i/5)*maxVal)}</text>
          </g>;
        })}

        {/* Area fill + line behind bars */}
        {areaPath && <path d={areaPath} fill="url(#areaFill)" />}
        {linePath && <path d={linePath} fill="none" stroke={C.indigo} strokeWidth={2} strokeLinecap="round" opacity={.35} />}

        {/* Bars per month */}
        {months.map((m,i) => {
          const cx = P.l + i*gap + gap/2;
          const fV=forecast[m]||0, aV=actuals[m]||0, pV=pending[m]||0;
          const fH=(fV/maxVal)*iH, aH=(aV/maxVal)*iH, pH=(pV/maxVal)*iH;
          const baseY = P.t+iH;
          const hasA = aV>0||pV>0;
          const isH = hI===i;

          return <g key={m}
            onMouseMove={e=>{setHI(i);show(e,m);}}
            onMouseLeave={()=>{setHI(-1);setTip(null);}}
            style={{cursor:'pointer'}}
          >
            <rect x={cx-gap/2} y={P.t} width={gap} height={iH}
              fill={isH?'rgba(99,102,241,.04)':'transparent'} />

            {/* Forecast bar */}
            <rect x={cx-(hasA?bW+2:bW/2)} y={baseY-fH} width={bW} height={Math.max(fH,0)}
              rx={4} fill="url(#gBar)" filter={isH?'url(#bs)':undefined}
              style={{transition:'opacity .15s'}} opacity={isH?1:.8} />

            {/* Actual bar */}
            {aV>0 && <rect x={cx+2} y={baseY-aH} width={bW} height={Math.max(aH,0)}
              rx={4} fill="url(#gBarA)" filter={isH?'url(#bs)':undefined} opacity={isH?1:.8} />}

            {/* Pending bar */}
            {pV>0&&!aV && <rect x={cx+2} y={baseY-pH} width={bW} height={Math.max(pH,0)}
              rx={4} fill="url(#gBarP)" filter={isH?'url(#bs)':undefined} opacity={isH?1:.8} />}

            {/* Value label on hover */}
            {isH && fH>8 && <text x={cx-(hasA?bW/2+2:0)} y={baseY-fH-8} textAnchor="middle"
              fill={C.indigo} fontSize={10} fontWeight={700} fontFamily="system-ui">{$s(fV)}</text>}

            {/* Month label */}
            <text x={cx} y={baseY+20} textAnchor="middle"
              fill={isH?(t?.heading||C.slate700):(t?.textSecondary||C.slate500)} fontSize={10} fontWeight={isH?700:400} fontFamily="system-ui">
              {fM(m)}
            </text>
          </g>;
        })}
      </svg>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   STACKED CATEGORY CHART
   ═══════════════════════════════════════════════ */
const CategoryChart = ({ months, cb, cats, maxVal, t }) => {
  const ref = useRef(null);
  const [tip, setTip] = useState(null);
  const [hI, setHI] = useState(-1);
  const cW = useContainerWidth(ref);

  const P = {t:28,r:16,b:48,l:62};
  const H = 300, W = Math.max(months.length*72, cW);
  const iW=W-P.l-P.r, iH=H-P.t-P.b;
  const gap=iW/months.length, bW=Math.min(32,gap*.5);

  const show = (e,m) => {
    const rc=ref.current.getBoundingClientRect();
    const rows = cats.filter(c=>(cb[m]?.[c]||0)>0).map(c=>({label:c,value:$f(cb[m][c]),color:gc(c).s}));
    const total = rows.reduce((s,r)=>s+(+r.value.replace(/[^0-9.-]/g,'')||0),0);
    setTip({x:e.clientX-rc.left,y:e.clientY-rc.top,title:fM(m),rows});
  };

  return (
    <div ref={ref} style={{position:'relative',overflowX:'auto',overflowY:'visible'}}>
      <Tip info={tip} />
      <svg width={W} height={H} style={{display:'block'}}>
        <defs>
          {cats.map(cat=>(
            <linearGradient key={cat} id={`gc_${cat.replace(/\s/g,'')}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={gc(cat).s} />
              <stop offset="100%" stopColor={gc(cat).l} />
            </linearGradient>
          ))}
        </defs>

        {Array.from({length:6},(_,i)=>{
          const y=P.t+iH-(i/5)*iH;
          const gridColor = t?.dark ? 'rgba(255,255,255,.06)' : C.grid;
          const borderColor = t?.dark ? 'rgba(255,255,255,.12)' : C.border;
          return <g key={i}>
            <line x1={P.l} x2={W-P.r} y1={y} y2={y} stroke={i===0?borderColor:gridColor} />
            <text x={P.l-10} y={y+4} textAnchor="end" fill={t?.textSecondary||C.slate500} fontSize={10} fontFamily="system-ui">{$s((i/5)*maxVal)}</text>
          </g>;
        })}

        {months.map((m,i) => {
          const cx=P.l+i*gap+gap/2, baseY=P.t+iH;
          let cum=0; const isH=hI===i;
          return <g key={m}
            onMouseMove={e=>{setHI(i);show(e,m);}}
            onMouseLeave={()=>{setHI(-1);setTip(null);}}
            style={{cursor:'pointer'}}
          >
            <rect x={cx-gap/2} y={P.t} width={gap} height={iH} fill={isH?'rgba(99,102,241,.04)':'transparent'} />
            {cats.map(cat=>{
              const val=cb[m]?.[cat]||0;
              if(val<=0)return null;
              const segH=(val/maxVal)*iH;
              const segY=baseY-cum-segH;
              cum+=segH;
              return <rect key={cat} x={cx-bW/2} y={segY} width={bW} height={Math.max(segH,0)}
                rx={3} fill={`url(#gc_${cat.replace(/\s/g,'')})`}
                opacity={isH?1:.75} style={{transition:'opacity .15s'}} />;
            })}
            <text x={cx} y={baseY+20} textAnchor="middle" fill={isH?(t?.heading||C.slate700):(t?.textSecondary||C.slate500)}
              fontSize={10} fontWeight={isH?700:400} fontFamily="system-ui">{fM(m)}</text>
          </g>;
        })}
      </svg>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   S-CURVE — Smooth area chart with earned value style
   ═══════════════════════════════════════════════ */
const SCurve = ({ months, forecast, actuals, totalContract, t }) => {
  const ref = useRef(null);
  const [tip, setTip] = useState(null);
  const [hI, setHI] = useState(-1);
  const cW = useContainerWidth(ref);

  const P = {t:28,r:70,b:48,l:62};
  const H = 320, W = Math.max(months.length*72, cW);
  const iW=W-P.l-P.r, iH=H-P.t-P.b;
  const maxY = (totalContract>0?totalContract:1)*1.1;

  // Burn-down: remaining = totalContract - cumulative billed
  let cF=0, cA=0;
  const fP = months.map((m,i) => {
    cF+=forecast[m]||0;
    const remaining = totalContract - cF;
    return {x:P.l+(i/Math.max(months.length-1,1))*iW, y:P.t+iH-(remaining/maxY)*iH, v:remaining, billed:cF, m};
  });
  const aP = []; cA=0;
  months.forEach((m,i)=>{ if(actuals[m]>0){ cA+=actuals[m]; const remaining=totalContract-cA; aP.push({x:P.l+(i/Math.max(months.length-1,1))*iW, y:P.t+iH-(remaining/maxY)*iH, v:remaining, billed:cA, m}); }});

  const smooth = (pts) => {
    if(pts.length<2) return pts.length===1?`M${pts[0].x},${pts[0].y}`:'';
    let d=`M${pts[0].x},${pts[0].y}`;
    for(let i=1;i<pts.length;i++){
      const p=pts[i-1],c=pts[i],cpx=(p.x+c.x)/2;
      d+=` C${cpx},${p.y} ${cpx},${c.y} ${c.x},${c.y}`;
    } return d;
  };

  // Contract line at top (flat)
  const contractY = P.t+iH-(totalContract/maxY)*iH;
  // $0 target line at bottom
  const zeroY = P.t+iH;

  const fLine = smooth(fP);
  const aLine = smooth(aP);
  // Area fill between contract line (top) and burn-down curve
  const fArea = fP.length>1 ? `M${fP[0].x},${contractY} L${fP[0].x},${fP[0].y} `+
    fP.slice(1).map((p,i)=>{const prev=fP[i];const cpx=(prev.x+p.x)/2;return`C${cpx},${prev.y} ${cpx},${p.y} ${p.x},${p.y}`;}).join(' ')+
    ` L${fP[fP.length-1].x},${contractY} Z` : '';
  const aArea = aP.length>1 ? `M${aP[0].x},${contractY} L${aP[0].x},${aP[0].y} `+
    aP.slice(1).map((p,i)=>{const prev=aP[i];const cpx=(prev.x+p.x)/2;return`C${cpx},${prev.y} ${cpx},${p.y} ${p.x},${p.y}`;}).join(' ')+
    ` L${aP[aP.length-1].x},${contractY} Z` : '';

  const show = (e,idx) => {
    const rc=ref.current.getBoundingClientRect();
    const fp=fP[idx];
    const aMatch=aP.find(p=>p.m===months[idx]);
    const rows = [
      {label:'Proj. Remaining',value:$f(fp?.v||0),color:C.indigo},
      {label:'Billed to Date',value:$f(fp?.billed||0),color:C.slate500},
    ];
    if(aMatch) {
      rows.push({label:'Act. Remaining',value:$f(aMatch.v),color:C.emerald});
      const diff=(fp?.v||0)-aMatch.v;
      rows.push({label:'Variance',value:`${diff>=0?'+':''}${$f(diff)}`,color:diff>=0?C.emerald:C.rose});
    }
    setTip({x:e.clientX-rc.left,y:e.clientY-rc.top,title:fM(months[idx]),rows});
  };

  return (
    <div ref={ref} style={{position:'relative',overflowX:'auto',overflowY:'visible'}}>
      <Tip info={tip} />
      <svg width={W} height={H} style={{display:'block'}}>
        <defs>
          <linearGradient id="fAreaG" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.indigo} stopOpacity=".12" />
            <stop offset="100%" stopColor={C.indigo} stopOpacity=".03" />
          </linearGradient>
          <linearGradient id="aAreaG" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.emerald} stopOpacity=".10" />
            <stop offset="100%" stopColor={C.emerald} stopOpacity=".02" />
          </linearGradient>
          <filter id="lineGlow"><feDropShadow dx="0" dy="0" stdDeviation="4" floodColor={C.indigo} floodOpacity=".2"/></filter>
          <filter id="dotG"><feDropShadow dx="0" dy="0" stdDeviation="4" floodOpacity=".2"/></filter>
        </defs>

        {/* Grid */}
        {Array.from({length:6},(_,i)=>{
          const y=P.t+iH-(i/5)*iH;
          const gridColor = t?.dark ? 'rgba(255,255,255,.06)' : C.grid;
          const borderColor = t?.dark ? 'rgba(255,255,255,.12)' : C.border;
          return <g key={i}>
            <line x1={P.l} x2={W-P.r} y1={y} y2={y} stroke={i===0?borderColor:gridColor} />
            <text x={P.l-10} y={y+4} textAnchor="end" fill={t?.textSecondary||C.slate500} fontSize={10} fontFamily="system-ui">{$s((i/5)*maxY)}</text>
          </g>;
        })}

        {/* Contract value line at top */}
        {totalContract>0 && <g>
          <line x1={P.l} x2={W-P.r} y1={contractY} y2={contractY} stroke={C.rose} strokeWidth={1.5} strokeDasharray="8,6" opacity={.5} />
          <rect x={W-P.r+6} y={contractY-11} width={60} height={22} rx={6} fill={C.rose} opacity={.08} />
          <text x={W-P.r+10} y={contractY+4} fill={C.rose} fontSize={9} fontWeight={700} fontFamily="system-ui">Contract</text>
        </g>}

        {/* $0 target line at bottom */}
        <g>
          <line x1={P.l} x2={W-P.r} y1={zeroY} y2={zeroY} stroke={C.emerald} strokeWidth={1} strokeDasharray="6,4" opacity={.35} />
          <rect x={W-P.r+6} y={zeroY-11} width={48} height={22} rx={6} fill={C.emerald} opacity={.08} />
          <text x={W-P.r+10} y={zeroY+4} fill={C.emerald} fontSize={9} fontWeight={700} fontFamily="system-ui">$0 Target</text>
        </g>

        {/* Area fills (between contract and burn-down line) */}
        {fArea && <path d={fArea} fill="url(#fAreaG)" />}
        {aArea && <path d={aArea} fill="url(#aAreaG)" />}

        {/* Burn-down lines */}
        <path d={fLine} fill="none" stroke={C.indigo} strokeWidth={2.5} strokeLinecap="round" filter="url(#lineGlow)" />
        {aLine && <path d={aLine} fill="none" stroke={C.emerald} strokeWidth={2.5} strokeLinecap="round" />}

        {/* Interactive zones + dots */}
        {months.map((m,i) => {
          const x=P.l+(i/Math.max(months.length-1,1))*iW;
          const isH=hI===i;
          const aMatch=aP.find(p=>p.m===m);
          return <g key={m}
            onMouseMove={e=>{setHI(i);show(e,i);}}
            onMouseLeave={()=>{setHI(-1);setTip(null);}}
            style={{cursor:'pointer'}}
          >
            <rect x={x-20} y={P.t} width={40} height={iH+P.b} fill="transparent" />
            {isH && <line x1={x} x2={x} y1={P.t} y2={P.t+iH} stroke={C.indigo} strokeWidth={1} opacity={.12} strokeDasharray="4,4" />}

            {/* Forecast remaining dot */}
            <circle cx={fP[i].x} cy={fP[i].y} r={isH?6:3.5}
              fill={t?.bg||C.white} stroke={C.indigo} strokeWidth={isH?3:2}
              filter={isH?'url(#dotG)':undefined}
              style={{transition:'r .12s ease'}} />

            {/* Actual remaining dot */}
            {aMatch && <circle cx={aMatch.x} cy={aMatch.y} r={isH?6:3.5}
              fill={t?.bg||C.white} stroke={C.emerald} strokeWidth={isH?3:2}
              filter={isH?'url(#dotG)':undefined}
              style={{transition:'r .12s ease'}} />}

            {/* X label */}
            {(months.length<=14||i%2===0||i===months.length-1) &&
              <text x={x} y={P.t+iH+20} textAnchor="middle" fill={isH?(t?.heading||C.slate700):(t?.textSecondary||C.slate500)}
                fontSize={10} fontWeight={isH?700:400} fontFamily="system-ui">{fM(m)}</text>}
          </g>;
        })}
      </svg>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   LEGEND
   ═══════════════════════════════════════════════ */
const Legend = ({ items, t }) => (
  <div style={{ display:'flex', gap:16, flexWrap:'wrap', alignItems:'center' }}>
    {items.map(it=>(
      <div key={it.label} style={{ display:'flex', alignItems:'center', gap:7 }}>
        {it.type==='line' ? (
          <svg width={20} height={10}><line x1={0} y1={5} x2={20} y2={5} stroke={it.color} strokeWidth={2.5}
            strokeDasharray={it.dashed?'6,4':'none'} strokeLinecap="round" /></svg>
        ) : (
          <div style={{ width:10, height:10, borderRadius:3, background:`linear-gradient(135deg,${it.color},${it.light||it.color})` }} />
        )}
        <span style={{ fontSize:11, color:t?.textSecondary||C.slate500, fontWeight:500 }}>{it.label}</span>
      </div>
    ))}
  </div>
);

/* ═══════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════ */
const ForecastTab = ({ projectId, refreshSummary }) => {
  const t = useTheme();
  const { confirm, confirmDialogProps } = useConfirm();
  const [dashboard, setDashboard] = useState(null);
  const [forecasts, setForecasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeChart, setActiveChart] = useState('overview');

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [d,f] = await Promise.all([getForecastDashboard(projectId), getForecasts(projectId)]);
      setDashboard(d); setForecasts(f); setError(null);
    } catch(err){ setError('Failed to load forecast data'); console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchAll();
  }, [projectId]);

  const handleDel = async (id) => {
    if(await confirm({title:'Delete Entry',message:'Remove this forecast entry?',confirmLabel:'Delete',danger:true})){
      try { await deleteForecast(id); await fetchAll(); refreshSummary(); }
      catch{ setError('Failed to delete'); }
    }
  };

  const cd = useMemo(() => {
    if(!dashboard) return null;
    const {monthlyForecast:mf,monthlyActuals:ma,monthlyPending:mp,categoryBreakdown:cb,categories:cats,totalContract:tc,totalRetainage:tR} = dashboard;
    const months = [...new Set([...Object.keys(mf),...Object.keys(mp)])].sort();
    const maxBar = Math.max(...months.map(m=>Math.max(mf[m]||0,mp[m]||0)),1)*1.15;
    const maxStack = Math.max(...months.map(m=>cb[m]?Object.values(cb[m]).reduce((s,v)=>s+v,0):0),1)*1.15;
    const tF=Object.values(mf).reduce((s,v)=>s+v,0);
    const tA=Object.values(ma||{}).reduce((s,v)=>s+v,0);
    const tP=Object.values(mp).reduce((s,v)=>s+v,0);
    // Highest & lowest forecast months
    const forecastMonths = Object.entries(mf).filter(([,v]) => v > 0);
    let highMonth = null, lowMonth = null;
    if (forecastMonths.length > 0) {
      highMonth = forecastMonths.reduce((best, cur) => cur[1] > best[1] ? cur : best);
      lowMonth = forecastMonths.reduce((best, cur) => cur[1] < best[1] ? cur : best);
    }
    return {months,maxBar,maxStack,tF,tA,tP,tc,tR:tR||0,mf,ma:ma||{},mp,cb,cats,highMonth,lowMonth};
  }, [dashboard]);

  /* Spinner */
  if(loading) return (
    <div style={{padding:'80px 20px',textAlign:'center'}}>
      <div style={{width:44,height:44,border:`3px solid ${t.dark?'rgba(255,255,255,.1)':C.slate100}`,borderTopColor:C.indigo,borderRadius:'50%',margin:'0 auto 16px',animation:'spin .7s linear infinite'}} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{color:t.textSecondary,fontSize:14,fontWeight:500}}>Generating forecast...</div>
    </div>
  );

  const pctForecast = cd&&cd.tc>0 ? (cd.tF/cd.tc*100) : 0;
  const pctBilled = cd&&cd.tc>0 ? (cd.tA/cd.tc*100) : 0;

  const card = { background:t.bg, borderRadius:16, border:`1px solid ${t.cardBorder}`, padding:'24px 28px', marginBottom:20, boxShadow:'0 1px 4px rgba(0,0,0,.03)' };
  const pill = (active) => ({
    padding:'8px 20px', borderRadius:24, border:'none',
    background:active?C.indigo:'transparent', color:active?'#fff':t.textSecondary,
    fontWeight:600, fontSize:13, cursor:'pointer', transition:'all .15s',
    boxShadow:active?'0 2px 10px rgba(99,102,241,.3)':'none',
  });

  return (
    <div>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:28}}>
        <div>
          <h2 style={{margin:0,fontSize:24,fontWeight:800,color:t.heading,letterSpacing:'-.4px'}}>Billing Forecast</h2>
          {dashboard?.projectDates?.start && (
            <div style={{fontSize:12,color:t.textSecondary,marginTop:6}}>
              {fM(dashboard.projectDates.start?.slice(0,7))} — {fM(dashboard.projectDates.end?.slice(0,7))}
            </div>
          )}
        </div>
      </div>

      {error && <div style={{padding:'12px 16px',background:'#fef2f2',border:'1px solid #fecaca',borderRadius:12,color:C.rose,fontSize:13,marginBottom:16}}>{error}</div>}

      {/* KPI Row — Mastt-style donut rings + summary */}
      {cd && (
        <div style={{...card, display:'flex', gap:32, flexWrap:'wrap', alignItems:'center', padding:'28px 32px'}}>
          <ProgressRing pct={pctBilled} color={C.emerald} label="Billed to Date" value={$f(cd.tA)} sub={`of ${$f(cd.tc)}`} subExtra={cd.tR > 0 ? `+${$f(cd.tR)} retainage held` : null} t={t} />
          <ProgressRing pct={100 - pctBilled} color={C.indigo} label="Remaining" value={$f(cd.tc - cd.tA)} sub={`${(100 - pctBilled).toFixed(1)}% of contract`} t={t} />
          <div style={{borderLeft:`1px solid ${t.tableBorder}`,height:50,margin:'0 8px'}} />
          <div>
            <div style={{fontSize:10,fontWeight:700,color:t.textSecondary,textTransform:'uppercase',letterSpacing:.8}}>Pending Review</div>
            <div style={{fontSize:20,fontWeight:800,color:C.amber,marginTop:3}}>{$f(cd.tP)}</div>
          </div>
          <div style={{borderLeft:`1px solid ${t.tableBorder}`,height:50,margin:'0 8px'}} />
          <div>
            <div style={{fontSize:10,fontWeight:700,color:t.textSecondary,textTransform:'uppercase',letterSpacing:.8}}>Avg. Billing / Month</div>
            <div style={{fontSize:20,fontWeight:800,color:C.indigo,marginTop:3}}>{$f(cd.months.length>0 ? cd.tF/cd.months.length : 0)}</div>
          </div>
          {cd.highMonth && (
            <>
              <div style={{borderLeft:`1px solid ${t.tableBorder}`,height:50,margin:'0 8px'}} />
              <div>
                <div style={{fontSize:10,fontWeight:700,color:t.textSecondary,textTransform:'uppercase',letterSpacing:.8}}>Highest Billed Month</div>
                <div style={{fontSize:20,fontWeight:800,color:C.emerald,marginTop:3}}>{$f(cd.highMonth[1])}</div>
                <div style={{fontSize:11,color:t.textSecondary,marginTop:1}}>{fM(cd.highMonth[0])}</div>
              </div>
            </>
          )}
          {cd.lowMonth && (
            <>
              <div style={{borderLeft:`1px solid ${t.tableBorder}`,height:50,margin:'0 8px'}} />
              <div>
                <div style={{fontSize:10,fontWeight:700,color:t.textSecondary,textTransform:'uppercase',letterSpacing:.8}}>Lowest Billed Month</div>
                <div style={{fontSize:20,fontWeight:800,color:C.rose,marginTop:3}}>{$f(cd.lowMonth[1])}</div>
                <div style={{fontSize:11,color:t.textSecondary,marginTop:1}}>{fM(cd.lowMonth[0])}</div>
              </div>
            </>
          )}
        </div>
      )}


      {/* Chart Tabs — pill switcher */}
      <div style={{display:'flex',gap:4,marginBottom:20,background:t.bgAlt,borderRadius:28,padding:4,width:'fit-content'}}>
        {[{k:'overview',l:'Monthly'},{k:'category',l:'By Category'},{k:'scurve',l:'Burn Down'}].map(tab=>(
          <button key={tab.k} style={pill(activeChart===tab.k)} onClick={()=>setActiveChart(tab.k)}>{tab.l}</button>
        ))}
      </div>

      {/* Charts */}
      {cd && cd.months.length > 0 ? (
        <>
          {activeChart==='overview' && (
            <div style={card}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
                <h3 style={{margin:0,fontSize:15,fontWeight:700,color:t.heading}}>Monthly Projected Billing</h3>
                <Legend items={[
                  {label:'Projected',color:C.indigo,light:C.indigoL},
                  {label:'Pending Review',color:C.amber,light:C.amberL},
                ]} t={t} />
              </div>
              <MonthlyChart months={cd.months} forecast={cd.mf} actuals={{}} pending={cd.mp} maxVal={cd.maxBar} t={t} />
            </div>
          )}

          {activeChart==='category' && (
            <div style={card}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
                <h3 style={{margin:0,fontSize:15,fontWeight:700,color:t.heading}}>Forecast by Category</h3>
                <Legend items={cd.cats.map(c=>({label:c,color:gc(c).s,light:gc(c).l}))} t={t} />
              </div>
              <CategoryChart months={cd.months} cb={cd.cb} cats={cd.cats} maxVal={cd.maxStack} t={t} />
            </div>
          )}

          {activeChart==='scurve' && (
            <div style={card}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
                <h3 style={{margin:0,fontSize:15,fontWeight:700,color:t.heading}}>Burn-Down Curve</h3>
                <Legend items={[
                  {label:'Proj. Remaining',color:C.indigo,type:'line'},
                  {label:'Contract',color:C.rose,type:'line',dashed:true},
                ]} t={t} />
              </div>
              <SCurve months={cd.months} forecast={cd.mf} actuals={{}} totalContract={cd.tc} t={t} />
            </div>
          )}

          {activeChart==='table' && (
            <div style={card}>
              <h3 style={{margin:'0 0 16px',fontSize:15,fontWeight:700,color:t.heading}}>Forecast Detail</h3>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'separate',borderSpacing:'0 4px',fontSize:13}}>
                  <thead>
                    <tr>
                      {['Month','SOV Line','Proj. %','Earned','Stored','Total','Source',''].map((h,i)=>(
                        <th key={h} style={{
                          padding:'10px 14px',fontSize:10,fontWeight:700,textTransform:'uppercase',
                          letterSpacing:.6,color:t.textSecondary,borderBottom:`2px solid ${t.tableBorder}`,
                          textAlign:i>=2&&i<=5?'right':i===6?'center':'left',
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {forecasts.length===0 ? (
                      <tr><td colSpan={8} style={{padding:40,textAlign:'center',color:t.textSecondary}}>No entries yet.</td></tr>
                    ) : forecasts.map((f,i)=>{
                      const total=(+f.projected_earned_amount||0)+(+f.projected_stored_materials||0);
                      return (
                        <tr key={f.id} style={{background:i%2?t.bgAlt:t.bg,borderRadius:8,color:t.text}}>
                          <td style={{padding:'11px 14px',fontWeight:600,borderRadius:'8px 0 0 8px',color:t.heading}}>{fM(f.forecast_month)}</td>
                          <td style={{padding:'11px 14px',maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',color:t.text}}>{f.sov_line_id||'—'}</td>
                          <td style={{padding:'11px 14px',textAlign:'right',color:t.text}}>{(+f.projected_percent_complete||0).toFixed(1)}%</td>
                          <td style={{padding:'11px 14px',textAlign:'right',color:t.text}}>{$f(f.projected_earned_amount)}</td>
                          <td style={{padding:'11px 14px',textAlign:'right',color:t.text}}>{$f(f.projected_stored_materials)}</td>
                          <td style={{padding:'11px 14px',textAlign:'right',fontWeight:700,color:t.heading}}>{$f(total)}</td>
                          <td style={{padding:'11px 14px',textAlign:'center'}}>
                            <span style={{
                              fontSize:10,fontWeight:700,padding:'4px 12px',borderRadius:12,
                              background:f.source==='auto'?C.indigoBg:C.amberBg,
                              color:f.source==='auto'?C.indigo:C.amber,
                            }}>{f.source==='auto'?'Auto':'Manual'}</span>
                          </td>
                          <td style={{padding:'11px 14px',textAlign:'center',borderRadius:'0 8px 8px 0'}}>
                            <button onClick={()=>handleDel(f.id)} style={{
                              background:'none',border:'none',color:t.textSecondary,cursor:'pointer',
                              fontSize:16,lineHeight:1,padding:4,borderRadius:6,transition:'color .12s',
                            }}
                              onMouseEnter={e=>e.currentTarget.style.color=C.rose}
                              onMouseLeave={e=>e.currentTarget.style.color=t.textSecondary}
                            >×</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : (
        <div style={{...card,textAlign:'center',padding:'70px 20px'}}>
          <div style={{fontSize:52,marginBottom:16,opacity:.3}}>📊</div>
          <div style={{fontSize:17,fontWeight:700,color:t.textSecondary,marginBottom:8}}>No Forecast Data</div>
          <div style={{fontSize:13,color:t.textSecondary,maxWidth:380,margin:'0 auto',lineHeight:1.7}}>
            Forecast data is auto-generated from your task schedule and SOV allocations. Make sure tasks have start/end dates.
          </div>
        </div>
      )}

      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
};

export default ForecastTab;
